<template>
	<div class="flow-app-widget" :tabindex="tabindex" @click="handleClick($event)">
		<div class="flow-app-name-n-state">
			<div class="flow-app-name">
				<div class="flow-app-state-indicator" :state="state" />
				<h4 class="fc-dark line-h-14 text-ellipsis">
					{{ name }}
				</h4>
			</div>
			<div class="flow-app-state">
				<p class="paragraph-2 fc-light line-h-12 text-ellipsis">
					{{ status }}
				</p>
			</div>
		</div>
		<div class="flow-app-git-details">
			<div class="flow-app-git-commit">
				<Icon
					:name="iconNames.commit"
					state="white"
					size="x-small"
					:effects="false"
					type="filled"
				/>
				<p class="paragraph-2 fc-light line-h-12 text-ellipsis">
					{{ commit }}
				</p>
			</div>
			<div class="flow-app-git-branch">
				<Icon
					:name="iconNames.branch"
					state="white"
					size="x-small"
					:effects="false"
					type="filled"
				/>
				<p class="paragraph-2 fc-light line-h-12 text-ellipsis">
					{{ branch }}
				</p>
			</div>
		</div>
		<div class="flow-app-cost-n-server">
			<div class="flow-app-cost">
				<Icon :name="iconNames.cost" state="white" size="x-small" :effects="false" type="filled" />
				<h5 class="fc-dark">
					{{ cost }}
				</h5>
			</div>
			<div class="flow-app-server">
				<Icon
					:name="iconNames.server"
					state="white"
					size="x-small"
					:effects="false"
					type="filled"
				/>
				<p class="paragraph-2 fc-light line-h-12 text-ellipsis">
					{{ server }}
				</p>
			</div>
		</div>
		<div class="flow-app-state-icons">
			<slot name="icons" />
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Icon from "./../Icon/Icon";

export default defineComponent({
	name: "Application",
	components: {
		Icon
	},
	props: {
		/**
		 *  'default' | "primary" | "success" | "error" | "warning"
		 */
		state: {
			type: String,
			default: "default",
			validator(value) {
				return ["default", "primary", "success", "error", "warning"].includes(value);
			}
		},
		/**
		 * Application name
		 */
		name: {
			type: String,
			default: null
		},
		/**
		 * Application status e.g. Last build on 10 Dec 03:56
		 */
		status: {
			type: String,
			default: null
		},
		/**
		 * Commit id
		 */
		commit: {
			type: String,
			default: null
		},
		/**
		 * Source code branch name
		 */
		branch: {
			type: String,
			default: null
		},
		/**
		 * Server cost
		 */
		cost: {
			type: String,
			default: null
		},
		/**
		 * Server type/name
		 */
		server: {
			type: String,
			default: null
		},
		/**
		 * { commit: 'git-commit',branch:"git-branch",cost:"doller",server:"aws" }
		 */
		iconNames: {
			type: Object,
			default() {
				return { commit: "git-commit", branch: "git-branch", cost: "doller", server: "aws" };
			}
		},
		/**
		 * tabindex number
		 *  */ tabindex: {
			type: Number,
			default: -1
		}
	},
	methods: {
		handleClick(e) {
			/**
			 * Click event
			 *
			 * @event click
			 */
			this.$emit("click", e);
		}
	}
});
</script>
<style lang="scss">
$app-states: (
	"default": var(--dashboard-icon),
	"primary": var(--dashboard-primary),
	"success": var(--dashboard-success),
	"error": var(--dashboard-danger),
	"warning": var(--dashboard-warning)
);

div.flow-app-widget {
	display: flex;
	border-radius: 8px;
	background-color: var(--dashboard-element-light);
	padding: 16px;
	justify-content: space-between;
	align-items: center;
	min-width: 450px;

	> div.flow-app-name-n-state {
		display: flex;
		flex-direction: column;
		max-width: 40%;
		> *:first-child {
			margin-bottom: 8px;
		}

		> div.flow-app-name {
			display: flex;
			align-items: center;
			> div.flow-app-state-indicator {
				height: 16px;
				width: 16px;
				border-radius: 4px;
				margin-right: 8px;
				flex-shrink: 0;

				@each $state, $color in $app-states {
					&[state="#{$state}"] {
						background-color: $color;
					}
				}
			}
		}
	}
	> div.flow-app-git-details {
		display: flex;
		flex-direction: column;
		max-width: 30%;
		> *:first-child {
			margin-bottom: 8px;
		}

		> div.flow-app-git-branch {
			display: flex;
			align-items: center;
			> *:first-child {
				margin-right: 8px;
			}
		}
		> div.flow-app-git-commit {
			display: flex;
			align-items: center;
			> *:first-child {
				margin-right: 8px;
			}
		}
	}
	> div.flow-app-cost-n-server {
		display: flex;
		flex-direction: column;
		max-width: 20%;
		> *:first-child {
			margin-bottom: 8px;
		}

		> div.flow-app-cost {
			display: flex;
			align-items: center;
			> *:first-child {
				margin-right: 8px;
			}
		}
		> div.flow-app-server {
			display: flex;
			align-items: center;
			> *:first-child {
				margin-right: 8px;
			}
		}
	}
	> div.flow-app-state-icons {
		display: flex;
		margin-top: -8px;
		margin-bottom: -8px;
		flex-direction: column;
		> *:first-child {
			margin-bottom: 8px;
		}

		// .app-w-icon {
		// 	height: 18px !important;
		// 	width: 18px !important;
		// }
	}

	&:active {
		filter: contrast(90%);
	}

	&:hover:not(:active) {
		filter: contrast(95%);
	}
	&:focus {
		box-shadow: inset 0 0 0 2px var(--dashboard-primary);
		outline: none;
	}
}
</style>
